import dayjs from 'dayjs';
import { convertHoursMinutesToSeconds } from './format';
import storage from './storage';
import { DELIVERY_CHARGE, DELIVERY_NEVER_FREE } from '@/constants/default';
import { Cart, CartCharges, CartItem } from '@/types/cart';

export const getCartInfo = (cartTotal: Cart) => {
  const result = Object.keys(cartTotal.cart_items).map((key) => {
    const totalItems = cartTotal.cart_items[key].reduce((acc, item) => acc + item.quantity, 0);
    const totalAmount = cartTotal.cart_items[key].reduce(
      (acc, item) => acc + item.quantity * item.price,
      0
    );
    return {
      vendorId: key,
      totalItems,
      totalAmount,
    };
  });
  return result;
};

export const getFormattedAmount = (amount: any) => {
  const currencySymbol = storage.getItem('currency_symbol') ?? '₹';
  return `${currencySymbol}${' '}${parseFloat(amount).toFixed(2)}`;
};

export const clearCartOrderingType = () => {
  sessionStorage.removeItem('GLOBAL_ORDER_TYPE');
  sessionStorage.removeItem('REMOVE_GLOBAL_ORDER_TYPE');
  sessionStorage.removeItem('VENDOR_ORDER_TYPE');
  sessionStorage.removeItem('ORDER_TYPE');
  sessionStorage.removeItem('INSTRUCTIONS_OPEN');
  sessionStorage.removeItem('isPreOrderPopupShown');
  sessionStorage.removeItem('customPreOrderTime');
  sessionStorage.removeItem('IS_CART_UPDATED_TEXT_SHOWN');
  sessionStorage.removeItem('cancellationNudgeShown');
  sessionStorage.removeItem('DELIVERY_ADDRESS');
};

export const removeCartStorage = () => {
  storage.removeItem('cartData');
  storage.removeItem('groupedCartData');
  storage.removeItem('vendorOrderType');
  storage.removeItem('formData');
  clearCartOrderingType();
};

export const getTotalOrderAmountWithoutAdditionalFees = (cart: Cart) => {
  let amount = 0;
  const vendorIds = Object.keys(cart.cart_vendors);
  vendorIds.forEach((vendorId) => {
    amount += getTotalOrderAmountWithoutAdditionalFeesOnVendor(cart, vendorId);
  });
  return amount;
};

export const getTotalOrderAmountWithoutAdditionalFeesOnVendor = (cart: Cart, vendorId: string) => {
  let amount = 0;
  const cartItems = getCartItemsForVendor(cart, vendorId);
  cartItems.forEach((cartItem) => {
    if (cartItem.add_gst_on_price) {
      amount += Number(cartItem.subtotal);
    } else amount += Number(cartItem.total);
  });
  return amount;
};

export const getCartItemsForVendor = (cart: Cart, vendorId: string) => {
  const cartItemsForVendor: CartItem[] = [];
  if (cart.cart_items[vendorId] != null) {
    cartItemsForVendor.push(...cart.cart_items[vendorId]);
  }

  // write logic for disabled cart items later

  return cartItemsForVendor;
};

export const getVendorById = (vendorId: string) => {
  const vendor = storage.getItem('vendorMap');
  return vendor[vendorId];
};

export const getApplicableDeliveryCharges = (cart: Cart, isCancelledDeliveryCharges: boolean) => {
  let applicableDeliveryFees = 0;
  const vendorIds = Object.keys(cart.cart_vendors);
  vendorIds.forEach((vendorId) => {
    if (cart.cart_vendors[vendorId].desk_ordering_enabled === 2) {
      applicableDeliveryFees += getDeliveryFeeOfVendor(cart, vendorId, isCancelledDeliveryCharges);
    }
  });
  return applicableDeliveryFees;
};

export const getCartChargeForVendor = (
  cartCharges: Record<string, CartCharges[]>,
  chargeKey: string,
  vendorId: string
) => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  let charges = {} as CartCharges;
  cartCharges?.[vendorId]?.forEach((cartCharge) => {
    if (cartCharge.charge_type === chargeKey) {
      charges = cartCharge;
      return cartCharge;
    }
  });
  return charges;
};

export const getDeliveryFeeOfVendor = (
  cart: Cart,
  vendorId: string,
  isCancelledDeliveryCharges = false
) => {
  const deliveryCharges = getCartChargeForVendor(cart.cart_charges, DELIVERY_CHARGE, vendorId);
  if (deliveryCharges === null) {
    return 0;
  }

  if (isCancelledDeliveryCharges) {
    return deliveryCharges?.total ?? 0;
  }
  if (
    deliveryCharges?.extra_amount_for_free_delivery !== DELIVERY_NEVER_FREE &&
    !deliveryCharges?.charge_applicable
  ) {
    return 0;
  }
  return deliveryCharges?.total ?? 0;
};

export const getCartCharge = (cart: Cart, chargeKey: string) => {
  let cartCharge = 0;
  const vendorIds = Object.keys(cart.cart_vendors);
  vendorIds.forEach((vendorId) => {
    const cartCharges = getCartChargeForVendor(cart.cart_charges, chargeKey, vendorId);
    if (cartCharges?.charge_applicable) {
      cartCharge += cartCharges?.total ?? 0;
    }
  });
  return cartCharge;
};

export const isCurrentOccasionOrder = (cartData: Cart | undefined) => {
  const allOccasions = storage.getItem('occasions');
  const vendor = cartData?.cart_vendors[Object.keys(cartData?.cart_vendors)[0]];
  const occasionId = cartData?.occasion_id;
  const occasion = allOccasions?.find((occasion: any) => occasion?.id === occasionId);
  const currentTime = dayjs().valueOf();
  if (vendor) {
    const vendorStartTime = convertHoursMinutesToSeconds(vendor?.start_time);
    if (occasion && currentTime > vendorStartTime && occasion?.preorder_day === 'today') {
      return true;
    }
  }
  return false;
};

export const getCategoryQuantity = (
  allCartItems: CartItem[] | undefined,
  categoryId: number,
  parentCategoryId?: number
) => {
  let quantity = 0;
  if (allCartItems) {
    allCartItems.forEach((item) => {
      if (item.parent_category_id) {
        if (item.parent_category_id === parentCategoryId) {
          quantity += item.quantity;
        }
      } else if (item.category_id === categoryId) {
        quantity += item.quantity;
      }
    });
  }
  return quantity;
};
